// @ts-nocheck
import * as React from "react";

// Chip 기반 태그 입력. backend는 여전히 comma-separated 문자열로 받기 때문에
// value/onChange는 단일 string으로 유지(모델 변경 0). 내부에서는 배열로 다룬다.
//
// 사용:
//   <TagsInput
//     value={form.services}
//     onChange={v => set('services')(v)}
//     placeholder="응급실 입력 후 Enter"
//   />
//
// 키바인딩:
//   Enter / Comma → 입력값을 chip으로 변환
//   Backspace (입력 비었을 때) → 마지막 chip 삭제
//   X 클릭 → 해당 chip 삭제
//
// 동작 규칙:
//   - trim 후 빈 문자열은 무시
//   - 동일 태그(대소문자 그대로) 중복 시 무시 — 운영 데이터 더러움 방지
//   - 붙여넣기에 comma 포함 시 자동 분할 (예: "응급실,내과" 한 번에 입력)

function parseTags(raw) {
  return String(raw || "")
    .split(",")
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

function toCsv(tags) {
  return tags.join(",");
}

export function TagsInput({ value, onChange, placeholder, disabled }) {
  const tags = React.useMemo(() => parseTags(value), [value]);
  const [draft, setDraft] = React.useState("");
  const inputRef = React.useRef(null);

  const commitDraft = (raw) => {
    const incoming = parseTags(raw);
    if (incoming.length === 0) return;
    const next = [...tags];
    for (const t of incoming) {
      if (!next.includes(t)) next.push(t);
    }
    onChange?.(toCsv(next));
    setDraft("");
  };

  const removeAt = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange?.(toCsv(next));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraft(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
      e.preventDefault();
      removeAt(tags.length - 1);
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (text.includes(",")) {
      e.preventDefault();
      commitDraft(text);
    }
  };

  const onBlur = () => {
    // 포커스 잃을 때도 작성 중 chip을 살림(타이핑만 하고 Enter 깜빡한 케이스).
    if (draft.trim().length > 0) commitDraft(draft);
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      style={{
        display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
        padding: "6px 8px", minHeight: 38,
        border: "1px solid var(--border-soft)", borderRadius: 8,
        background: disabled ? "var(--bg-sub)" : "var(--surface)",
        cursor: disabled ? "not-allowed" : "text",
      }}>
      {tags.map((t, i) => (
        <span key={`${t}-${i}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 6px 3px 10px",
            background: "var(--bg-sub)", borderRadius: 99,
            fontSize: 12, fontWeight: 600, color: "var(--ink-2)",
          }}>
          {t}
          {!disabled && (
            <button type="button" onClick={(e) => { e.stopPropagation(); removeAt(i); }}
              aria-label={`${t} 삭제`}
              style={{
                border: "none", background: "transparent", padding: 0,
                width: 16, height: 16, lineHeight: 1, fontSize: 14,
                color: "var(--ink-3)", cursor: "pointer",
              }}>
              ×
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={onBlur}
        placeholder={tags.length === 0 ? (placeholder || "Enter로 태그 추가") : ""}
        disabled={disabled}
        style={{
          flex: 1, minWidth: 120, border: "none", outline: "none",
          background: "transparent", fontSize: 12.5, padding: "4px 2px",
        }}
      />
    </div>
  );
}
